import React, { useContext, useState } from 'react'

// @mui core components
import { Box, Grid, Typography, Button } from '@mui/material'
import Card from 'atoms/Card'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'

import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { renderFormField } from 'utils/forms'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import LoadingButton from 'components/LoadingButton'

import TSPLoanAssets from 'components/TSPLoanAssets'

function Tsp() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  const [percentDollarsQuestion, setPercentDollarsQuestion] = useState(true)
  const [percentDollarsQuestionRoth, setPercentDollarsQuestionRoth] =
    useState(true)

  const dollarsToPercent = (contributionAmount) => {
    return intakeState?.baseSalary
      ? (((contributionAmount / 80) * 2087) / intakeState?.baseSalary) * 100
      : 0
  }
  const percentToDollars = (contributionPercent) => {
    return intakeState?.baseSalary
      ? (intakeState?.baseSalary / 2087) * 80 * (contributionPercent / 100)
      : 0
  }

  const [localForm, setLocalForm] = useState({
    tspBalance: intakeState.tspBalance || '',
    tspContribution: intakeState.tspContribution || '',
    tspContributionAmount: intakeState.tspContributionAmount || '',
    tspRothBalance: intakeState.tspRothBalance || '',
    tspRothContribution: intakeState.tspRothContribution || '',
    tspRothContributionAmount: intakeState.tspRothContributionAmount || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const showTSPLoans = intakeState?.tspLoans
  const showRothTSP = intakeState?.tspRoth
  const showTSPContribution = intakeState?.tsp

  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your deduction details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  return (
    <>
      <Box
        pl={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography display="flex" align="left" variant="body2FunHeader">
          TSP
        </Typography>

        <LoadingButton loading={loading} onClick={onSubmit} />
      </Box>
      <Card>
        <Box component="form" p={3}>
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">Traditional TSP Details</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'tsp',
                label: 'Do you contribute to the Traditional TSP?',
                placeholder: '',
                inputType: 'select',
                value: intakeState.tsp,
                onChange: updateIntakeStateAndSubmit((tsp) =>
                  updateIntakeStateAtKey({ tsp })
                ),
                options: selectData.yesNo,
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'tspBalance',
                label: 'Current Traditional TSP balance',
                placeholder: '$250,000.00',
                inputType: 'currencyFloat',
                value: localForm.tspBalance,
                onChange: (e) => handleChange('tspBalance')(e),
                onBlur: updateIntakeStateAndSubmit((tspBalance) =>
                  updateIntakeStateAtKey({ tspBalance })
                ),
              })}
            </Grid>
            {showTSPContribution && (
              <>
                {percentDollarsQuestion ? (
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'tspContribution',
                        label: 'What percentage do you contribute?',
                        placeholder: 'Ex. 5.0%',
                        inputType: 'percentage',
                        value: localForm.tspContribution,
                        onChange: (e) => {
                          handleChange('tspContribution')(e)
                          handleChange('tspContributionAmount')(
                            percentToDollars(e)
                          )
                        },
                        onBlur: updateIntakeStateAndSubmit((tspContribution) =>
                          updateIntakeStateAtKey({
                            tspContribution,
                            tspContributionAmount:
                              percentToDollars(tspContribution),
                          })
                        ),
                      })}
                    </Grid>
                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setPercentDollarsQuestion(false)}
                      >
                        Use amount contributed per pay period instead
                      </Button>
                    </Box>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'tspContributionAmount',
                        label: 'What amount do you contribute per pay period?',
                        placeholder: '$135.00',
                        inputType: 'currencyFloat',
                        value: localForm.tspContributionAmount,
                        onChange: (e) => {
                          handleChange('tspContributionAmount')(e)
                          handleChange('tspContribution')(dollarsToPercent(e))
                        },
                        onBlur: updateIntakeStateAndSubmit(
                          (tspContributionAmount) =>
                            updateIntakeStateAtKey({
                              tspContributionAmount,
                              tspContribution: dollarsToPercent(
                                tspContributionAmount
                              ),
                            })
                        ),
                      })}
                    </Grid>
                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setPercentDollarsQuestion(true)}
                      >
                        Use percentage contributed per pay period instead
                      </Button>
                    </Box>
                  </Grid>
                )}
              </>
            )}

            {/* ROTH TSP */}
            <Grid item xs={12} mt={2}>
              <Typography variant="h5">Roth TSP Details</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'tspRoth',
                label: 'Do you contribute to the Roth TSP?',
                placeholder: '',
                inputType: 'select',
                value: intakeState.tspRoth,
                onChange: updateIntakeStateAndSubmit((tspRoth) =>
                  updateIntakeStateAtKey({ tspRoth })
                ),
                options: selectData.yesNo,
              })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'tspRothBalance',
                label: 'Current Roth TSP balance',
                placeholder: '$250,000.00',
                inputType: 'currencyFloat',
                value: localForm.tspRothBalance,
                onChange: (e) => handleChange('tspRothBalance')(e),
                onBlur: updateIntakeStateAndSubmit((tspRothBalance) =>
                  updateIntakeStateAtKey({ tspRothBalance })
                ),
              })}
            </Grid>

            {showRothTSP && (
              <>
                {percentDollarsQuestionRoth ? (
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'tspRothContribution',
                        label: 'What percentage do you contribute?',
                        placeholder: 'Ex. 5.0%',
                        inputType: 'percentage',
                        value: localForm.tspRothContribution,
                        onChange: (e) => {
                          handleChange('tspRothContribution')(e)
                          handleChange('tspRothContributionAmount')(
                            percentToDollars(e)
                          )
                        },
                        onBlur: updateIntakeStateAndSubmit(
                          (tspRothContribution) =>
                            updateIntakeStateAtKey({
                              tspRothContribution,
                              tspRothContributionAmount:
                                percentToDollars(tspRothContribution),
                            })
                        ),
                      })}
                    </Grid>
                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setPercentDollarsQuestionRoth(false)}
                      >
                        Use amount contributed per pay period instead
                      </Button>
                    </Box>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <Grid item xs={12}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'tspRothContributionAmount',
                        label: 'What amount do you contribute per pay period?',
                        placeholder: '$135.00',
                        inputType: 'currencyFloat',
                        value: localForm.tspRothContributionAmount,
                        onChange: (e) => {
                          handleChange('tspRothContributionAmount')(e)
                          handleChange('tspRothContribution')(
                            dollarsToPercent(e)
                          )
                        },
                        onBlur: updateIntakeStateAndSubmit(
                          (tspRothContributionAmount) =>
                            updateIntakeStateAtKey({
                              tspRothContributionAmount,
                              tspRothContribution: dollarsToPercent(
                                tspRothContributionAmount
                              ),
                            })
                        ),
                      })}
                    </Grid>
                    <Box display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        color="info"
                        sx={{ height: '100%' }}
                        onClick={() => setPercentDollarsQuestionRoth(true)}
                      >
                        Use percentage contributed per pay period instead
                      </Button>
                    </Box>
                  </Grid>
                )}
              </>
            )}
            <Grid item xs={12} mt={2}>
              <Typography variant="h5">TSP Loan Details</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'tspLoans',
                label: 'Do you have any TSP loans?',
                placeholder: '',
                inputType: 'select',
                value: intakeState.tspLoans,
                onChange: updateIntakeStateAndSubmit((tspLoans) =>
                  updateIntakeStateAtKey({ tspLoans })
                ),
                options: selectData.yesNo,
              })}
            </Grid>

            {showTSPLoans && (
              <Grid item xs={12} sm={12}>
                <TSPLoanAssets />
              </Grid>
            )}
          </Grid>
        </Box>
      </Card>
    </>
  )
}

export default React.memo(Tsp)
